import { Component, Inject } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog, MatSnackBar } from '@angular/material';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { ConfirmacionComponent } from '../../confirmacion/confirmacion.component';

/**
 * Dialog para atender o anular una garantia D2D que ya fue tomada.
 *
 * Form de atender:
 *   - requiereFalla: toggle. Si esta off, el campo falla se oculta y se
 *     persiste como null. Si esta on, falla pasa a ser obligatoria con
 *     patron de 7 chars alfanumericos.
 *   - falla: 7 chars alfanum (auto-uppercase) — solo cuando toggle on
 *   - comentario_atencion: obligatorio
 *
 * Acciones:
 *   - "Efectivo" -> estado 1 -> 2 (mantiene icono check)
 *   - "Anular"   -> estado 1 -> 3 (abre confirmacion con comentario
 *                                   obligatorio que se guarda en BD)
 */
const FALLA_PATTERN = /^[A-Z0-9]{7}$/;

@Component({
  selector: 'app-atender-garantia-dialog',
  templateUrl: './atender-garantia-dialog.component.html',
  styleUrls: ['./atender-garantia-dialog.component.scss']
})
export class AtenderGarantiaDialogComponent {

  form: FormGroup;
  guardando = false;
  anulando = false;

  garantia: any;

  constructor(
    private dialogRef: MatDialogRef<AtenderGarantiaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private gateway: GatewayService,
    private localSave: LocalSaveService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.garantia = data && data.garantia ? data.garantia : {};
    // Si la garantia ya tiene falla, asumimos requiereFalla=true para que
    // el agente pueda re-validar al re-abrir el dialog. Por defecto off.
    const requiereInicial = !!(this.garantia.falla);
    this.form = new FormGroup({
      requiereFalla:       new FormControl(requiereInicial),
      falla:               new FormControl(this.garantia.falla || '',
                                            requiereInicial ? [Validators.required, Validators.pattern(FALLA_PATTERN)] : []),
      comentario_atencion: new FormControl(this.garantia.comentario_atencion || '', [Validators.required, Validators.maxLength(1000)]),
    });
  }

  /**
   * Maneja el cambio del toggle: agrega o quita los validators del campo
   * falla, y limpia el valor si se desactiva.
   */
  onRequiereFallaChange(checked: boolean): void {
    const fallaCtrl = this.form.controls.falla;
    if (checked) {
      fallaCtrl.setValidators([Validators.required, Validators.pattern(FALLA_PATTERN)]);
    } else {
      fallaCtrl.clearValidators();
      fallaCtrl.setValue('');
    }
    fallaCtrl.updateValueAndValidity();
  }

  // Auto-uppercase mientras tipea, igual que en ejecutar-certificado.
  onFallaInput(ev: any): void {
    const raw = ev && ev.target ? ev.target.value : '';
    const cleaned = String(raw || '').toUpperCase().replace(/\s+/g, '');
    if (cleaned !== raw) {
      this.form.controls.falla.setValue(cleaned, { emitEvent: false });
    }
  }

  atender(): void {
    if (this.form.invalid || this.guardando || this.anulando) { return; }
    this.guardando = true;
    const user = this.localSave.get('usuarioPGV') || {};
    const requiereFalla = !!this.form.controls.requiereFalla.value;
    const payload = {
      id:                  this.garantia.id,
      id_agente_tomo:      user.idagente != null ? user.idagente : user.IDU,
      // Si no requiere falla, mandamos string vacio -> el backend persiste null.
      falla:               requiereFalla ? String(this.form.controls.falla.value || '').toUpperCase() : '',
      comentario_atencion: this.form.controls.comentario_atencion.value,
    };
    this.gateway.get('garantiaD2dAtender', payload)
      .then((res: any) => {
        this.guardando = false;
        if (Number(res.response) === 1) {
          this.snackbar.open(res.message || 'Garantía atendida.', 'Cerrar', { duration: 2500 });
          this.dialogRef.close({ atendida: true });
        } else {
          this.snackbar.open(res.message || 'No se pudo atender.', 'Cerrar', { duration: 4000 });
        }
      })
      .catch(() => {
        this.guardando = false;
        this.snackbar.open('Error al atender la garantía.', 'Cerrar', { duration: 3000 });
      });
  }

  anular(): void {
    if (this.guardando || this.anulando) { return; }
    // Reusamos el comentario del form de atencion como motivo de anulacion.
    // Si esta vacio, pedimos al agente que lo complete antes (es el mismo
    // textarea visible en el dialog, no abrimos otro). El backend igual
    // exige comentario_anulacion no vacio.
    const comentRaw = this.form.controls.comentario_atencion.value;
    const coment = comentRaw ? String(comentRaw).trim() : '';
    if (!coment) {
      this.snackbar.open('Escribí el comentario antes de anular.', 'Cerrar', { duration: 3500 });
      this.form.controls.comentario_atencion.markAsTouched();
      return;
    }
    // Solo confirmacion simple (sin pedir comentario otra vez).
    const ref = this.dialog.open(ConfirmacionComponent, {
      disableClose: true,
      width: '380px',
      data: { sms: 'anular esta garantía', addcomment: false }
    });
    ref.afterClosed().subscribe((r: any) => {
      if (!r || !r.resp) { return; }
      this.anulando = true;
      const user = this.localSave.get('usuarioPGV') || {};
      const obj = user.OBJ || {};
      const nombreCombinado = ((obj.nombre || '') + ' ' + (obj.apellido || '')).trim();
      this.gateway.get('garantiaD2dAnular', {
        id: this.garantia.id,
        id_agente_tomo: user.idagente != null ? user.idagente : user.IDU,
        nombre_anulo:   nombreCombinado || this.localSave.get('userLoggedIn') || null,
        correo_anulo:   obj.correo || null,
        comentario_anulacion: coment,
      }).then((res: any) => {
        this.anulando = false;
        if (Number(res.response) === 1) {
          this.snackbar.open(res.message || 'Garantía anulada.', 'Cerrar', { duration: 2500 });
          this.dialogRef.close({ anulada: true });
        } else {
          this.snackbar.open(res.message || 'No se pudo anular.', 'Cerrar', { duration: 4000 });
        }
      }).catch(() => {
        this.anulando = false;
        this.snackbar.open('Error al anular la garantía.', 'Cerrar', { duration: 3000 });
      });
    });
  }

  cerrar(): void {
    this.dialogRef.close();
  }
}
